import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import ChartCard from "./ChartCard";
import DimensionTable from "./DimensionTable";
import { ChartRow, CountUnit, formatCount, formatPct } from "./types";

interface HBarCardProps {
	title: string;
	note?: string;
	rows: ChartRow[];
	unit: CountUnit;
	dimensionLabel: string;
	legend?: { label: string; color: string }[];
	labelWidth?: number;
}

const HBarCard: React.FC<HBarCardProps> = ({
	title,
	note,
	rows,
	unit,
	dimensionLabel,
	legend,
	labelWidth = 190
}) => {
	const { t, i18n } = useTranslation();

	const renderChart = () => {
		if (rows.length === 0) {
			return (
				<Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
					{t("communityDashboard.empty")}
				</Typography>
			);
		}
		const maxPct = Math.max(...rows.map(row => row.pct), 1);
		return (
			<Box display="flex" flexDirection="column">
				{rows.map(row => (
					<Tooltip
						key={row.id}
						title={`${formatCount(row.count, i18n.language)} · ${formatPct(row.pct, i18n.language)}`}
						placement="top"
						arrow
					>
						<Box
							display="grid"
							alignItems="center"
							gap={1.25}
							minHeight={26}
							px={0.5}
							sx={{
								gridTemplateColumns: {
									xs: "minmax(72px, 40%) 1fr minmax(44px, auto)",
									md: `${labelWidth}px 1fr 86px`
								},
								borderRadius: 1.5,
								"&:hover": { backgroundColor: zoullooColors.background.light }
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
								noWrap
								title={row.label}
							>
								{row.label}
							</Typography>
							<Box
								sx={{
									position: "relative",
									height: 16,
									borderLeft: `1px solid ${zoullooColors.border.light}`
								}}
							>
								<Box
									sx={{
										position: "absolute",
										left: 0,
										top: 0,
										height: 16,
										minWidth: 2,
										width: `${(row.pct * 100) / maxPct}%`,
										borderRadius: "0 4px 4px 0",
										backgroundColor: row.color,
										transition: "width 0.25s ease"
									}}
								/>
							</Box>
							<Typography variant="caption" fontWeight={700} color="text.primary">
								{unit === "values"
									? formatCount(row.count, i18n.language)
									: formatPct(row.pct, i18n.language)}
							</Typography>
						</Box>
					</Tooltip>
				))}
			</Box>
		);
	};

	return (
		<ChartCard
			title={title}
			note={note}
			legend={legend}
			renderChart={renderChart}
			renderTable={() => <DimensionTable rows={rows} dimensionLabel={dimensionLabel} />}
		/>
	);
};

export default HBarCard;
